import { useEffect, useMemo, useState, type ReactNode } from "react";

import {
  emptyCopyForPollTab,
  getPollGroupRows,
  sponsorPoll1IsComplete,
  type DiscoveryHubPollHubTab,
  type PollAccordionTabId,
} from "./pollAccordionHelpers";
import type { PollGroup } from "./PollCycleCard";
import TabsComponent from "../../Components/Tab";
import { DH_POLL_ACCORDION_SUBTABS } from "../../Utils/Helper/discoveryHubUi";

const POLL_SUB_TABS: { id: PollAccordionTabId; label: string }[] = [
  { id: "poll1", label: "Poll 1" },
  { id: "poll2", label: "Poll 2" },
];

type PollAccordionContentProps = {
  group: PollGroup & Record<string, unknown>;
  hubTab: DiscoveryHubPollHubTab;
  cycleId: string;
  showReminders?: boolean;
  renderSponsorProgressTable: (
    rows: any[],
    emptyTitle: string,
    emptyHint: string,
    cycleId?: string,
    showReminders?: boolean
  ) => ReactNode;
  renderOverdueTable: (
    rows: any[],
    emptyTitle: string,
    emptyHint: string,
    cycleId?: string,
    showReminders?: boolean,
    variant?: "sponsor" | "team-poll2"
  ) => ReactNode;
  renderTeamFeedbackTable: (
    rows: any[],
    emptyTitle: string,
    emptyHint: string,
    cycleId?: string,
    showReminders?: boolean
  ) => ReactNode;
};

const PollAccordionContent = ({
  group,
  hubTab,
  cycleId,
  showReminders = false,
  renderSponsorProgressTable,
  renderOverdueTable,
  renderTeamFeedbackTable,
}: PollAccordionContentProps) => {
  const poll1Enabled = group.poll1_enabled !== false;
  const poll2Enabled = group.poll2_enabled ?? false;
  const defaultPollTab: PollAccordionTabId = poll1Enabled
    ? "poll1"
    : poll2Enabled
      ? "poll2"
      : "poll1";
  const [pollTab, setPollTab] = useState<PollAccordionTabId>(defaultPollTab);

  useEffect(() => {
    if (!poll1Enabled && pollTab === "poll1") {
      setPollTab(poll2Enabled ? "poll2" : "poll1");
    }
    if (!poll2Enabled && pollTab === "poll2") {
      setPollTab(poll1Enabled ? "poll1" : "poll2");
    }
  }, [poll1Enabled, poll2Enabled, pollTab]);

  const tabs = useMemo(
    () =>
      POLL_SUB_TABS.map(tab => ({
        ...tab,
        disabled:
          (tab.id === "poll1" && !poll1Enabled) ||
          (tab.id === "poll2" && !poll2Enabled),
      })),
    [poll1Enabled, poll2Enabled]
  );

  const rows = getPollGroupRows(group, hubTab, pollTab);
  const { title, hint } = emptyCopyForPollTab(hubTab, pollTab);
  const remindersForTable = showReminders;
  const pendingPoll1Rows =
    hubTab === "late-window" && pollTab === "poll1"
      ? rows.filter(
          row => (row.total_accounts ?? 0) > 0 && !sponsorPoll1IsComplete(row)
        )
      : [];
  const showLateWindowPoll1Overdue = pendingPoll1Rows.length > 0;

  return (
    <div>
      <div className={DH_POLL_ACCORDION_SUBTABS}>
        <TabsComponent
          tabs={tabs}
          activeTab={pollTab}
          onTabChange={(id: string) => setPollTab(id as PollAccordionTabId)}
          variant="tab-underline"
          className="mb-0 whitespace-nowrap"
        />
      </div>
      {pollTab === "poll2"
        ? hubTab === "late-window"
          ? renderOverdueTable(
              rows,
              title,
              hint,
              cycleId,
              remindersForTable,
              "team-poll2"
            )
          : renderTeamFeedbackTable(
              rows,
              title,
              hint,
              cycleId,
              remindersForTable
            )
        : hubTab === "late-window"
          ? showLateWindowPoll1Overdue
            ? renderOverdueTable(
                pendingPoll1Rows,
                title,
                hint,
                cycleId,
                remindersForTable
              )
            : renderSponsorProgressTable(
                rows,
                title,
                hint,
                cycleId,
                remindersForTable
              )
          : renderSponsorProgressTable(
              rows,
              title,
              hint,
              cycleId,
              remindersForTable
            )}
    </div>
  );
};

export default PollAccordionContent;
